import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { LayoutGrid } from 'lucide-react'
import type { Category } from '@/types'
import { StoreImage } from '@/store/components/StoreImage'
import { SectionActionButton } from '@/store/components/SectionActionButton'
import { homeEase } from './motion'
import { cn } from '@/lib/utils'

interface HomeCategoriesProps {
  categories: Category[]
}

const ringSize =
  'h-[120px] w-[120px] sm:h-[142px] sm:w-[142px]'

const categoryTileClass =
  'group flex w-[120px] shrink-0 flex-col items-center gap-2 sm:w-[142px] focus-ring rounded-xl'

const categoryLabelClass =
  'w-full text-center text-xs font-semibold leading-tight gold-gradient-text sm:text-[13px]'

function categoryInitial(name: string) {
  return name.trim().charAt(0).toUpperCase() || '?'
}

function CategoryImageRing({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'gold-gradient-ring rounded-full p-[2.5px] shadow-md shadow-gold-500/20',
        'transition-transform duration-300 group-hover:scale-105',
        className
      )}
    >
      <div className="h-full w-full overflow-hidden rounded-full bg-[#FAF8F4] ring-2 ring-white">
        {children}
      </div>
    </div>
  )
}

export function HomeCategories({ categories }: HomeCategoriesProps) {
  const reduceMotion = useReducedMotion()
  const visible = categories.slice(0, 14)

  if (visible.length === 0) return null

  return (
    <section
      id="categories"
      className="relative overflow-hidden bg-[#FAF8F4]"
      aria-labelledby="categories-title"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute inset-0 bg-gradient-to-b from-white via-[#FAF8F4] to-[#FAF8F4]" />
        <div className="absolute inset-x-0 top-0 h-24 bg-[radial-gradient(ellipse_80%_100%_at_50%_0%,rgba(249,224,118,0.14),transparent)]" />
        <div className="absolute -left-20 top-8 h-44 w-44 rounded-full bg-gold-300/10 blur-3xl" />
        <div className="absolute -right-16 bottom-0 h-36 w-36 rounded-full bg-amber-200/10 blur-3xl" />
      </div>

      <div className="store-container relative pb-4 pt-6 md:pb-5 md:pt-8">
        <div className="mb-4 flex items-center justify-between gap-3 md:mb-5">
          <div>
            <p className="type-eyebrow text-[11px] md:text-xs">Browse</p>
            <h2
              id="categories-title"
              className="font-display text-xl font-bold tracking-tight gold-gradient-text sm:text-2xl md:text-3xl lg:text-4xl"
            >
              Shop by category
            </h2>
          </div>
          <SectionActionButton to="/shop" animateIn={false} className="hidden sm:block">
            View all
          </SectionActionButton>
        </div>

        <div className="relative sm:-mx-6 lg:-mx-8">
          <div
            className={cn(
              'flex gap-4 overflow-x-auto pb-1 pt-0.5 sm:gap-5 sm:px-6 lg:px-8',
              'snap-x snap-mandatory scroll-smooth',
              '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
            )}
          >
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: homeEase }}
              className="shrink-0 snap-start"
            >
              <Link to="/shop" className={categoryTileClass}>
                <CategoryImageRing className={ringSize}>
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-luxury-900 to-luxury-950">
                    <LayoutGrid className="h-8 w-8 text-gold-200 sm:h-9 sm:w-9" />
                  </div>
                </CategoryImageRing>
                <span className={categoryLabelClass}>All</span>
              </Link>
            </motion.div>

            {visible.map((cat, index) => (
              <motion.div
                key={cat.id}
                initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.4,
                  ease: homeEase,
                  delay: index * 0.04,
                }}
                className="shrink-0 snap-start"
              >
                <Link
                  to={`/shop?category=${encodeURIComponent(cat.name)}`}
                  className={categoryTileClass}
                >
                  <CategoryImageRing className={ringSize}>
                    {cat.image ? (
                      <StoreImage
                        src={cat.image}
                        alt=""
                        objectFit="cover"
                        sizing="box"
                        containerClassName="h-full w-full rounded-full"
                        className="h-full w-full rounded-full transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-luxury-800 to-luxury-950">
                        <span className="font-display text-3xl font-bold text-gold-300/50 sm:text-4xl">
                          {categoryInitial(cat.name)}
                        </span>
                      </div>
                    )}
                  </CategoryImageRing>
                  <span className={cn(categoryLabelClass, 'line-clamp-2')}>
                    {cat.name}
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
